import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Toolbar,
  AppBar,
  List,
  Paper,
  PaperProps,
  CardHeader,
  Card,
  CardContent,
} from "@mui/material";
import {
  Close,
  ZoomIn,
  ZoomOut,
  NavigateBefore,
  NavigateNext,
  Refresh,
  Menu,
  CenterFocusStrong,
  KeyboardTab,
  CenterFocusWeak,
  Download,
  Print,
} from "@mui/icons-material";
import { Document, Page, pdfjs } from "react-pdf";
import {
  isValidBase64,
  base64ToBlob,
  getMimeTypeFromFilename,
  validateImageDataHeader,
} from "../../utils/DocumentUtils";
import { DocumentDto, DocumentDtoDocuTypeEnum } from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import notificationStore from "../../stores/Notification/notificationStore";
import DocumentListItemViewer from "./DocumentListItemViewer";
import RxExtractionForm from "./ocr/RxExtractionForm";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { TopLevelPaper } from "../../themes/StyledComponents";
import useStore from "../../helpers/useStore";
import { UserContext } from "../../stores/User/User.provider";

// Document types that support OCR extraction
const EXTRACTABLE_DOCUMENT_TYPES: DocumentDtoDocuTypeEnum[] = [
  DocumentDtoDocuTypeEnum.Rezept,
];

// Helper to check if a document is extractable
const isDocumentExtractable = (docuType: string | undefined): boolean => {
  return docuType !== undefined && EXTRACTABLE_DOCUMENT_TYPES.includes(docuType as DocumentDtoDocuTypeEnum);
};

// Local interface for document data with base64 content
export interface DocumentData {
  id: string;
  name: string;
  mimeType: string;
  base64Data: string;
  createdAt: Date;
  docuType: string;
  accessUrl?: string;
  label?: string;
}

// Helper function to sort documents by date (newest first)
const sortDocumentsByDate = (documents: DocumentDto[]): DocumentDto[] => {
  return [...documents].sort((a, b) => 
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

// PDF.js Worker Configuration
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

// Draggable and Resizable Paper Component
function DraggablePaper(props: PaperProps) {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <Paper
      {...props}
      ref={nodeRef}
      sx={{
        ...props.sx,
        position: "relative",
        minWidth: "400px",
        minHeight: "300px",
        "& .MuiToolbar-root": {
          cursor: "move",
        },
        // Resize handles
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          right: 0,
          width: "20px",
          height: "20px",
          cursor: "nw-resize",
          background:
            "linear-gradient(-45deg, transparent 0%, transparent 40%, #999 40%, #999 45%, transparent 45%, transparent 55%, #999 55%, #999 60%, transparent 60%)",
          borderRadius: "0 0 4px 0",
          zIndex: 1000,
          "&:hover": {
            background:
              "linear-gradient(-45deg, transparent 0%, transparent 40%, #666 40%, #666 45%, transparent 45%, transparent 55%, #666 55%, #666 60%, transparent 60%)",
          },
        },
      }}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        const paper = nodeRef.current;
        if (!paper) return;

        const rect = paper.getBoundingClientRect();

        // Check if clicking on resize handle (bottom-right corner)
        const isResizeHandle = e.clientX > rect.right - 20 && e.clientY > rect.bottom - 20;

        if (isResizeHandle) {
          // Handle resizing
          const startWidth = rect.width;
          const startHeight = rect.height;
          const startX = e.clientX;
          const startY = e.clientY;

          const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const deltaY = moveEvent.clientY - startY;

            const newWidth = Math.max(400, startWidth + deltaX);
            const newHeight = Math.max(300, startHeight + deltaY);

            // Ensure dialog doesn't exceed viewport
            const maxWidth = window.innerWidth - rect.left;
            const maxHeight = window.innerHeight - rect.top;

            const clampedWidth = Math.min(newWidth, maxWidth);
            const clampedHeight = Math.min(newHeight, maxHeight);

            paper.style.width = `${clampedWidth}px`;
            paper.style.height = `${clampedHeight}px`;
          };

          const handleMouseUp = () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
          };

          document.addEventListener("mousemove", handleMouseMove);
          document.addEventListener("mouseup", handleMouseUp);

          e.preventDefault();
          e.stopPropagation();
          return;
        }

        // Handle dragging (only if clicking on the AppBar/Toolbar)
        const isToolbar = target.closest(".MuiAppBar-root") || target.closest(".MuiToolbar-root");

        if (!isToolbar) return;

        const startX = e.clientX - rect.left;
        const startY = e.clientY - rect.top;

        const handleMouseMove = (moveEvent: MouseEvent) => {
          const newX = moveEvent.clientX - startX;
          const newY = moveEvent.clientY - startY;

          // Ensure the dialog stays within viewport bounds
          const maxX = window.innerWidth - rect.width;
          const maxY = window.innerHeight - rect.height;

          const clampedX = Math.max(0, Math.min(newX, maxX));
          const clampedY = Math.max(0, Math.min(newY, maxY));

          paper.style.position = "fixed";
          paper.style.left = `${clampedX}px`;
          paper.style.top = `${clampedY}px`;
          paper.style.margin = "0";
          paper.style.transform = "none";
        };

        const handleMouseUp = () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        e.preventDefault();
      }}
    />
  );
}

interface SimpleDocumentViewerProps {
  open: boolean;
  onClose: () => void;
  patientId?: number;
  position?: "default" | "right-third";
  onPositionChange?: (position: "default" | "right-third") => void;
  initialDocumentId?: string; // New prop to specify which document to load initially
}

const SimpleDocumentViewer: React.FC<SimpleDocumentViewerProps> = ({
  open,
  onClose,
  patientId,
  position = "default",
  onPositionChange,
  initialDocumentId,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [autoFitScale, setAutoFitScale] = useState(1.0);
  const [imageNaturalSize, setImageNaturalSize] = useState<{ width: number; height: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Document loading state
  const [documents, setDocuments] = useState<DocumentDto[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [currentDocumentData, setCurrentDocumentData] = useState<DocumentData | null>(null);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [lastSelectedDocumentId, setLastSelectedDocumentId] = useState<string | null>(null);

  // Extraction state
  const [selectedDocumentDto, setSelectedDocumentDto] = useState<DocumentDto | null>(null);

  // Feature preview permission
  const UserStore = useStore(UserContext);
  const canSeeFeaturePreview = UserStore.hasPermission("featurePreview");

  // API hooks for document operations
  const { documents: documentsApi } = useApi();

  // Load documents when component opens
  useEffect(() => {
    if (open && patientId) {
      loadDocuments();
    }
  }, [open, patientId]);

  // Load documents for patient
  const loadDocuments = async () => {
    if (!patientId) return;

    setDocumentsLoading(true);
    try {
      const docs = await documentsApi.getDocuments(patientId);
      const sortedDocs = sortDocumentsByDate(docs as DocumentDto[]);
      setDocuments(sortedDocs);

      // Priority order for document selection:
      // 1. initialDocumentId (if provided and found)
      // 2. lastSelectedDocumentId (if found)
      // 3. First document (fallback)
      if (sortedDocs.length > 0) {
        let documentToLoad = sortedDocs[0]; // Default fallback

        // First priority: look for initialDocumentId
        if (initialDocumentId) {
          const initialDoc = sortedDocs.find((doc) => doc.id === initialDocumentId);
          if (initialDoc) {
            documentToLoad = initialDoc;
          }
        }
        // Second priority: look for lastSelectedDocumentId (only if no initialDocumentId)
        else if (lastSelectedDocumentId) {
          const lastDoc = sortedDocs.find((doc) => doc.id === lastSelectedDocumentId);
          if (lastDoc) {
            documentToLoad = lastDoc;
          }
        }

        await loadDocumentContent(documentToLoad);
      } else {
        setCurrentDocumentData(null);
        setSelectedDocument(null);
        console.info("Keine Dokumente für Patient verfügbar:", patientId);
      }
    } catch (error) {
      console.error("Error loading documents:", error);
      notificationStore.showMessage("Fehler beim Laden der Dokumente", "error");
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Load content of a specific document
  const loadDocumentContent = async (document: DocumentDto) => {
    setLoading(true);

    // Reset scale when loading new document
    setScale(1.0);
    setAutoFitScale(1.0);
    setPageNumber(1);

    try {
      const response = await documentsApi.requestViewAccessForDocument(document.id);
      
      if (!response || !(response as any).base64Data) {
        throw new Error('No document data received from server');
      }

      const docData: DocumentData = {
        id: document.id,
        name: document.name,
        mimeType: (response as any).mimeType || getMimeTypeFromFilename(document.name),
        base64Data: (response as any).base64Data,
        createdAt: new Date(document.createdAt),
        docuType: document.docuType as string,
        label: document.label || document.name,
      };

      setCurrentDocumentData(docData);
      setSelectedDocument(docData);
      // Track the full DocumentDto for extraction panel
      setSelectedDocumentDto(document);
      // Remember this document as the last selected one
      setLastSelectedDocumentId(document.id);
    } catch (error) {
      console.error("Error loading document content:", error);
      notificationStore.showMessage("Fehler beim Laden des Dokumentinhalts", "error");
    } finally {
      setLoading(false);
    }
  };



  // Blob-URL immer mit dem tatsächlich verwendeten MIME-Type erzeugen
  useEffect(() => {
    if (currentDocumentData?.base64Data) {
      // Ermittele den tatsächlichen MIME-Type wie in der Anzeige-Logik
      let mimeType =
        currentDocumentData?.mimeType || getMimeTypeFromFilename(currentDocumentData?.name || "");
      if (
        (mimeType === "image/heic" || mimeType === "image/heif") &&
        validateImageDataHeader(currentDocumentData.base64Data, "image/png")
      ) {
        mimeType = "image/png";
      }
      if (mimeType === "application/octet-stream" || mimeType === "binary/octet-stream") {
        if (validateImageDataHeader(currentDocumentData.base64Data, "image/png")) {
          mimeType = "image/png";
        } else if (validateImageDataHeader(currentDocumentData.base64Data, "image/jpeg")) {
          mimeType = "image/jpeg";
        }
      }
      const blob = base64ToBlob(currentDocumentData.base64Data, mimeType);
      if (blob) {
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } else {
        setBlobUrl(null);
      }
    } else {
      setBlobUrl(null);
    }
    // Clean up alte Blob-URL
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDocumentData]);

  // PDF event handlers
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    // Calculate auto-fit scale after document loads
    setTimeout(() => calculateAutoFitScale(), 100);
  };

  const calculateAutoFitScale = () => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerWidth = container.clientWidth - 40; // Account for padding
    const containerHeight = container.clientHeight - 40;

    // For PDF, we need to find the page element
    const pageElement = container.querySelector(".react-pdf__Page__canvas") as HTMLCanvasElement;
    if (pageElement) {
      const pageWidth = pageElement.width;
      const pageHeight = pageElement.height;

      const scaleX = containerWidth / pageWidth;
      const scaleY = containerHeight / pageHeight;
      const optimalScale = Math.min(scaleX, scaleY, 2.0); // Cap at 200%

      setAutoFitScale(optimalScale);
      setScale(optimalScale);
    } else {
      // For images, calculate based on natural dimensions
      const imageElement = container.querySelector("img") as HTMLImageElement;
      if (imageElement && imageElement.naturalWidth && imageElement.naturalHeight) {
        const scaleX = containerWidth / imageElement.naturalWidth;
        const scaleY = containerHeight / imageElement.naturalHeight;
        const optimalScale = Math.min(scaleX, scaleY, 2.0); // Cap at 200%

        setAutoFitScale(optimalScale);
        setScale(optimalScale);
      }
    }
  };

  // Recalculate scale when window resizes or sidebar toggles
  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => calculateAutoFitScale(), 100);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Recalculate when sidebar visibility changes
    setTimeout(() => calculateAutoFitScale(), 100);
  }, [sidebarVisible]);

  const resetToFit = () => {
    setScale(autoFitScale);
  };

  // Navigation handlers
  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(numPages || 1, prev + 1));
  };

  // Zoom handlers
  const zoomIn = () => {
    setScale((prev) => Math.min(3.0, prev + 0.2));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(0.1, prev - 0.2));
  };

  const handleClose = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    // Don't reset lastSelectedDocumentId - keep it for next time
    setCurrentDocumentData(null);
    setSelectedDocument(null);
    setSelectedDocumentDto(null);
    setDocuments([]);
    setPageNumber(1);
    setScale(1.0);
    setDocumentsLoading(false);
    setLoading(false);
    onClose();
  };

  // State für Position, falls kein onPositionChange übergeben wird
  const [internalPosition, setInternalPosition] = useState<"default" | "right-third">(position);
  const effectivePosition = onPositionChange ? position : internalPosition;

  const handlePositionToggle = () => {
    if (onPositionChange) {
      onPositionChange(position === "right-third" ? "default" : "right-third");
    } else {
      setInternalPosition(effectivePosition === "right-third" ? "default" : "right-third");
    }
  };

  // Determine file type
  const mimeType =
    currentDocumentData?.mimeType || getMimeTypeFromFilename(currentDocumentData?.name || "");
  let actualMimeType = mimeType;
  // Prüfe, ob wir einen PNG-Fallback anzeigen (z.B. HEIC-MIME aber PNG-Daten)
  if (
    currentDocumentData?.base64Data &&
    (mimeType === "image/heic" || mimeType === "image/heif") &&
    validateImageDataHeader(currentDocumentData.base64Data, "image/png")
  ) {
    actualMimeType = "image/png";
  }
  // NEU: Wenn application/octet-stream, prüfe auf Bilddaten
  if (
    currentDocumentData?.base64Data &&
    (mimeType === "application/octet-stream" || mimeType === "binary/octet-stream")
  ) {
    if (validateImageDataHeader(currentDocumentData.base64Data, "image/png")) {
      actualMimeType = "image/png";
    } else if (validateImageDataHeader(currentDocumentData.base64Data, "image/jpeg")) {
      actualMimeType = "image/jpeg";
    }
  }
  const actualIsPDF = actualMimeType.includes("pdf");
  const actualIsImage = actualMimeType.startsWith("image/");

  // Entferne das open-Prop, steuere Sichtbarkeit nur über display
  if (!open) return null;
  return (
    <DraggablePaper
      style={{
        position: effectivePosition === "right-third" ? "fixed" : "fixed",
        zIndex: 1300,
        width:
          effectivePosition === "right-third" ? `${Math.floor(window.innerWidth / 3)}px` : "80vw",
        height: effectivePosition === "right-third" ? `calc(100vh - 56px)` : "80vh", // 56px Abstand nach unten
        minWidth: "300px",
        minHeight: "300px",
        maxWidth: "none",
        maxHeight: "none",
        left:
          effectivePosition === "right-third"
            ? `${window.innerWidth - Math.floor(window.innerWidth / 3)}px`
            : "50%",
        top: effectivePosition === "right-third" ? "56px" : "50%", // 56px Abstand von oben
        margin: 0,
        transform: effectivePosition === "right-third" ? "none" : "translate(-50%, -50%)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        border: "1px solid #e0e0e0",
        background: "#000",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppBar position="static">
        <Toolbar>
          {/* Sidebar Toggle Button */}
          <IconButton
            onClick={() => setSidebarVisible(!sidebarVisible)}
            title={sidebarVisible ? "Dokumentenliste ausblenden" : "Dokumentenliste einblenden"}
            sx={{
              mr: 1,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <Menu />
          </IconButton>

          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "white",
            }}
          >
            {currentDocumentData?.label || "Dokumente"}
          </Typography>

          {/* Document controls - Only show if enough space */}
          {currentDocumentData && (
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              {currentDocumentData && blobUrl && (
                <>
                  {/* Download Button */}
                  <IconButton
                    onClick={() => {
                      const link = document.createElement("a");
                      link.href = blobUrl;
                      link.download = currentDocumentData?.name || "document";
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                    title="Herunterladen"
                    sx={{
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    <Download />
                  </IconButton>

                  {/* Print Button */}
                  <IconButton
                    onClick={() => {
                      const printWindow = window.open(blobUrl);
                      if (printWindow) {
                        printWindow.addEventListener("load", () => {
                          printWindow.print();
                        });
                      }
                    }}
                    title="Drucken"
                    sx={{
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    <Print />
                  </IconButton>
                </>
              )}
              {/* Zoom Controls */}
              <IconButton
                onClick={zoomOut}
                title="Verkleinern"
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <ZoomOut />
              </IconButton>
              <Typography
                variant="body2"
                sx={{ mx: 1, minWidth: "45px", textAlign: "center", color: "white" }}
              >
                {Math.round(scale * 100)}%
              </Typography>
              <IconButton
                onClick={zoomIn}
                title="Vergrößern"
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <ZoomIn />
              </IconButton>

              {/* Fit to Window Button */}
              <IconButton
                onClick={resetToFit}
                title="An Fenster anpassen"
                sx={{
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                <CenterFocusStrong />
              </IconButton>

              {/* PDF Navigation */}
              {actualIsPDF && numPages && (
                <>
                  <IconButton
                    onClick={goToPrevPage}
                    disabled={pageNumber <= 1}
                    title="Vorherige Seite"
                    sx={{
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      "&:disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <Typography variant="body2" sx={{ mx: 1, color: "white" }}>
                    {pageNumber} / {numPages}
                  </Typography>
                  <IconButton
                    onClick={goToNextPage}
                    disabled={pageNumber >= numPages}
                    title="Nächste Seite"
                    sx={{
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      },
                      "&:disabled": {
                        color: "rgba(255, 255, 255, 0.3)",
                      },
                    }}
                  >
                    <NavigateNext />
                  </IconButton>
                </>
              )}
            </Box>
          )}

          {/* Position Toggle - Dock Right / Center */}
          <IconButton
            onClick={handlePositionToggle}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
              mr: 1,
            }}
            title={effectivePosition === "right-third" ? "Zur Standardposition" : "Rechts andocken"}
          >
            {effectivePosition === "right-third" ? <CenterFocusWeak /> : <KeyboardTab />}
          </IconButton>

          {/* Close Button - Far right */}
          <IconButton
            onClick={handleClose}
            title="Schließen"
            sx={{
              flexShrink: 0,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <Close />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: "flex", p: 0, overflow: "hidden", height: "calc(100% - 64px)", flex: 1 }}>
        {/* Document List Sidebar */}
        {sidebarVisible && (
          <Card
            sx={{
              width: 280,
              borderRight: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <CardHeader
              title={
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography variant="h6">Dokumente</Typography>
                  <IconButton
                    size="small"
                    onClick={loadDocuments}
                    disabled={documentsLoading}
                    title="Aktualisieren"
                  >
                    <Refresh />
                  </IconButton>
                </Box>
              }
            />
            <CardContent
              sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", p: 0 }}
            >
              {/* Document List */}
              <Box sx={{ flex: 1, overflow: "auto" }}>
                {documentsLoading ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Lade Dokumente...
                    </Typography>
                  </Box>
                ) : documents.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Keine Dokumente verfügbar
                    </Typography>
                  </Box>
                ) : (
                  <List dense>
                    {documents.map((doc, index) => (
                      <DocumentListItemViewer
                        key={doc.id}
                        document={doc}
                        index={index}
                        onSelect={loadDocumentContent}
                        selected={selectedDocument?.id === doc.id}
                      />
                    ))}
                  </List>
                )}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Main Document Viewer */}
        <TopLevelPaper
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            height: "100%",
          }}
        >
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!loading && currentDocumentData && (
            <Box
              ref={containerRef}
              sx={{
                flex: 1,
                overflow: "auto",
                p: 1,
                height: "100%",
                "& .react-pdf__Document": {
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: "100%",
                  minHeight: "100%",
                },
                "& .react-pdf__Page": {
                  maxWidth: "none !important",
                  maxHeight: "none !important",
                },
                "& .react-pdf__Page__canvas": {
                  maxWidth: "none !important",
                  maxHeight: "none !important",
                  width: "auto !important",
                  height: "auto !important",
                },
              }}
            >
              {actualIsPDF && blobUrl && (
                <Document
                  file={blobUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<CircularProgress />}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    width={undefined}
                    height={undefined}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              )}

              {actualIsImage && blobUrl && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minWidth: "100%",
                    minHeight: "100%",
                  }}
                >
                  <img
                    src={blobUrl}
                    alt={currentDocumentData.name}
                    onLoad={(e) => {
                      const img = e.currentTarget;
                      setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
                      setTimeout(() => calculateAutoFitScale(), 50);
                    }}
                    style={{
                      width: imageNaturalSize ? imageNaturalSize.width * scale : "auto",
                      height: imageNaturalSize ? imageNaturalSize.height * scale : "auto",
                      maxWidth: "none",
                      maxHeight: "none",
                      transition: "width 0.2s ease, height 0.2s ease",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}

              {!actualIsPDF && !actualIsImage && (
                <Box sx={{ textAlign: "center", p: 4 }}>
                  <Typography variant="h6" color="error">
                    Nicht unterstütztes Dateiformat
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    MIME-Type: {currentDocumentData.mimeType}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {!loading && !currentDocumentData && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Wählen Sie ein Dokument aus der Liste aus
              </Typography>
            </Box>
          )}
        </TopLevelPaper>

        {/* Extraction Panel - shown when viewing an extractable document */}
        {canSeeFeaturePreview && selectedDocumentDto && isDocumentExtractable(selectedDocumentDto.docuType) && patientId && (
          <Card
            sx={{
              width: 380,
              borderLeft: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <RxExtractionForm
              document={selectedDocumentDto}
              patientId={patientId}
              onSaveSuccess={(rxId, isNew) => {
                // Optionally refresh or update state after save
                // console.log(`Rx ${isNew ? 'created' : 'updated'}: ${rxId}`);
              }}
            />
          </Card>
        )}
      </Box>
    </DraggablePaper>
  );
};

export default SimpleDocumentViewer;
